const XML_INDENT = '  ';

// Keep escaped data distinct from markup when the formatted XML is highlighted.
const escapeXmlText = (value: string): string =>
  value.replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;');

const isParserError = (document: XMLDocument): boolean =>
  document.getElementsByTagName('parsererror').length > 0;

const serializeElement = (element: Element, depth: number): string => {
  const serializedAttributes = Array.from(element.attributes)
    .map(({ name, value }) => ` ${name}="${escapeXmlText(value).replaceAll('"', '&quot;')}"`)
    .join('');
  const childNodes = Array.from(element.childNodes);
  const textOnly = childNodes.every((childNode) => childNode.nodeType === Node.TEXT_NODE);
  const openingTag = `<${element.tagName}${serializedAttributes}>`;

  if (textOnly) {
    return `${XML_INDENT.repeat(depth)}${openingTag}${childNodes
      .map((childNode) => escapeXmlText(childNode.nodeValue ?? ''))
      .join('')}</${element.tagName}>`;
  }

  const childXml = childNodes
    .map((childNode) => serializeNode(childNode, depth + 1))
    .filter(Boolean)
    .join('\n');

  return [
    `${XML_INDENT.repeat(depth)}${openingTag}`,
    childXml,
    `${XML_INDENT.repeat(depth)}</${element.tagName}>`,
  ].join('\n');
};

const serializeNode = (node: ChildNode, depth: number): string => {
  if (node.nodeType === Node.ELEMENT_NODE) return serializeElement(node as Element, depth);
  if (node.nodeType === Node.PROCESSING_INSTRUCTION_NODE) {
    return `${XML_INDENT.repeat(depth)}<?${node.nodeName}${node.nodeValue ? ` ${node.nodeValue}` : ''}?>`;
  }
  if (node.nodeType === Node.COMMENT_NODE) {
    return `${XML_INDENT.repeat(depth)}<!--${node.nodeValue ?? ''}-->`;
  }
  if (node.nodeType === Node.CDATA_SECTION_NODE) {
    return `${XML_INDENT.repeat(depth)}<![CDATA[${node.nodeValue ?? ''}]]>`;
  }
  return node.nodeType === Node.TEXT_NODE && node.nodeValue?.trim()
    ? `${XML_INDENT.repeat(depth)}${escapeXmlText(node.nodeValue.trim())}`
    : '';
};

/**
 * Formats CAP XML for readable display without changing the source copied by the user.
 *
 * @param xml CAP XML text.
 * @returns Indented XML, or the original text when it is empty or invalid.
 */
export const formatCapXml = (xml: string | null | undefined): string => {
  if (!xml?.trim()) return xml ?? '';

  const document = new DOMParser().parseFromString(xml, 'application/xml');
  if (isParserError(document) || !document.documentElement) return xml;

  const xmlDeclaration = xml.match(/^\s*(<\?xml\s[^?]*\?>)/)?.[1];
  const formattedDocument = serializeElement(document.documentElement, 0);
  const formattedXml = xmlDeclaration
    ? `${xmlDeclaration}\n${formattedDocument}`
    : formattedDocument;

  return formattedXml || xml;
};

export default formatCapXml;
